import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchUnsynchronizedCoreRetryStatus } from '../services/adminAnalyticsApi';
import {
  clearCoreRetryJob,
  getCoreRetrySnapshot,
  publishCoreRetryJob,
  subscribeCoreRetry,
} from '../services/coreRetryBus';
import '../styles/analytics-theme.css';

function CoreRetryBackground({ onShowNotification, onOpenList }) {
  const [snap, setSnap] = useState(getCoreRetrySnapshot);
  const notifyRef = useRef(onShowNotification);
  notifyRef.current = onShowNotification;

  useEffect(() => subscribeCoreRetry(setSnap), []);

  useEffect(() => {
    if (!snap.jobId) return undefined;
    let stopped = false;
    let timer = null;
    let finished = false;

    const poll = async () => {
      try {
        const job = await fetchUnsynchronizedCoreRetryStatus(snap.jobId);
        if (stopped) return;
        if (job.status === 'running') {
          publishCoreRetryJob(job);
          return;
        }
        if (timer) clearInterval(timer);
        if (finished) return;
        finished = true;
        clearCoreRetryJob();
        const tone = job.status === 'failed' ? 'error' : job.failed > 0 ? 'warning' : 'success';
        if (notifyRef.current) {
          notifyRef.current(job.message || 'Core retry finished', tone, tone === 'success' ? 7000 : 12000);
        }
      } catch (error) {
        if (stopped || finished) return;
        finished = true;
        if (timer) clearInterval(timer);
        clearCoreRetryJob();
        if (notifyRef.current) notifyRef.current(error.message, 'error', 12000);
      }
    };

    poll();
    timer = setInterval(poll, 2500);
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
  }, [snap.jobId]);

  const job = snap.job;
  const running = job?.status === 'running' || (snap.jobId && !job);
  if (!running) return null;

  const processed = job?.processed || 0;
  const total = job?.total || 0;
  const submitted = job?.submitted || 0;
  const failed = job?.failed || 0;

  return (
    <div className="ab-core-retry-bar" role="status">
      <Loader2 size={16} className="ab-spin" />
      <div className="ab-core-retry-copy">
        <strong>Sending policies to core</strong>
        <span>
          {total ? `${processed} of ${total}` : 'Starting'}
          {' · '}
          {submitted} submitted
          {' · '}
          {failed} failed
          {job?.pendingReview ? ` · ${job.pendingReview} for review` : ''}
        </span>
      </div>
      <button type="button" className="ab-btn sm" onClick={onOpenList}>
        Open list
      </button>
    </div>
  );
}

export default CoreRetryBackground;
